import React from "react";
import { ArrowDownIcon, CheckIcon } from "lucide-react";
import { CTAButton } from "../CTAButton";
import { imagineSteps, searchLoop } from "../../data/framework";
export function Problem() {
  return <>
      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20" data-id="element-301" data-mp="2rndc8xwmoztb">
        <div className="mx-auto max-w-2xl" data-id="element-302" data-mp="2mpk5o3rey3si">
          <p className="text-center text-sm font-bold uppercase tracking-[0.14em] text-alert" data-id="element-303" data-mp="2moiulzaedhas">
            The problem isn’t that you don’t know enough
          </p>
          <h2 className="mt-3 text-center font-display text-4xl font-extrabold uppercase leading-[1.02] text-ink sm:text-5xl" data-id="element-304" data-mp="2cqmjh7hgmmco">
            It’s that your clinical references aren’t organized into one clear assessment process.
          </h2>

          <div className="mt-8 space-y-4 text-lg leading-relaxed text-ink" data-id="element-305" data-mp="23s0v2vpb4d3z">
            <p data-id="element-306" data-mp="6x4rk2jjug2y">Think about the last time you needed to review a hair loss presentation.</p>
            <p data-id="element-307" data-mp="1ytgxplfk1jo">Maybe you wanted to compare two conditions with similar clinical characteristics.</p>
            <p data-id="element-308" data-mp="14ashxb13baqt">Maybe you needed to revisit a specific trichoscopic finding.</p>
            <p data-id="element-309" data-mp="199wpxyca32fk">
              Or perhaps you wanted to organize your differential diagnosis before deciding what
              additional information was necessary.
            </p>
          </div>

          <ol className="mt-8 space-y-2" aria-label="The search loop" data-id="element-310" data-mp="1096y15q51w74">
            {searchLoop.map((step, i) => <li key={step} className="flex flex-col items-center" data-id="element-311" data-mp="1nyszy0xwapit" data-mpi={i}>
                <span className="w-full rounded-xl bg-mist px-4 py-3 text-center text-lg font-semibold text-ink" data-id="element-312" data-mp="1s0mursleszb2">
                  {step}
                </span>
                {i < searchLoop.length - 1 && <ArrowDownIcon className="my-1 h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-313" data-mp="1j82qocmcm2iu" />}
              </li>)}
          </ol>

          <div className="mt-8 space-y-4 text-lg leading-relaxed text-ink" data-id="element-314" data-mp="26wm2h24lwxfo">
            <p data-id="element-315" data-mp="2bpf5nlkffxpn">
              And before you knew it, you were spending{" "}
              <strong data-id="element-316" data-mp="2lkvw4sh9grje">more time organizing information than reviewing the clinical question itself.</strong>
            </p>
            <p className="text-2xl font-bold" data-id="element-317" data-mp="2gmu95wgps82n">Sound familiar?</p>
            <p data-id="element-318" data-mp="2blwu3sr03u5d">The problem isn’t necessarily a lack of knowledge.</p>
            <p data-id="element-319" data-mp="2bo9cg70o86iy">
              It’s the <strong data-id="element-320" data-mp="26nv1ag3rwggp">absence of a consistent framework</strong> for organizing the
              information you’re reviewing.
            </p>
            <p data-id="element-321" data-mp="22nb2z97gr6sz">
              That’s exactly what <strong data-id="element-322" data-mp="1xv1i47l7gwxk">ALOPECIA CLINICAL BLUEPRINT™</strong> is designed to
              address. Instead of navigating scattered educational resources, you can follow a
              structured five-stage assessment framework.
            </p>
          </div>

          <div className="mt-8 space-y-2 border-l-4 border-brand pl-5" data-id="element-323" data-mp="1nyezlpgcqhyp">
            <p className="font-display text-2xl font-bold uppercase text-ink" data-id="element-324" data-mp="1iy0r66au9605">One clear sequence.</p>
            <p className="font-display text-2xl font-bold uppercase text-ink" data-id="element-325" data-mp="1evxc77oynja4">
              One organized reference collection.
            </p>
            <p className="font-display text-2xl font-bold uppercase text-brand" data-id="element-326" data-mp="3qlloil7khnol">
              One practical system for reviewing the information relevant to hair loss assessment.
            </p>
          </div>

          <CTAButton className="mt-10" label="I want one clear system" note="Just $27 USD · One-time payment" data-id="element-327" data-mp="3d076l2jb5zh" />
        </div>
      </section>

      <section className="bg-brand-soft px-4 py-16 sm:px-6 sm:py-20" data-id="element-328" data-mp="36wbb5dvanetn">
        <div className="mx-auto max-w-2xl" data-id="element-329" data-mp="3bud2sn6scmgq">
          <h2 className="text-center font-display text-4xl font-extrabold uppercase leading-[1.02] text-ink sm:text-5xl" data-id="element-330" data-mp="3bvefew3g9h0k">
            Imagine your next initial review…
          </h2>
          <ol className="mt-8 space-y-3" data-id="element-331" data-mp="3llhhdlsju5b1">
            {imagineSteps.map(step => <li key={step} className="flex items-start gap-3 rounded-2xl bg-white p-4 text-lg font-medium leading-snug text-ink" data-id="element-332" data-mp="2o5jzu6rqxecm">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success text-white" data-id="element-333" data-mp="2t2k8mpfzcv7f">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-334" data-mp="ylu0azf0q5yv" />
                </span>
                {step}
              </li>)}
          </ol>
          <p className="mt-8 text-center text-lg leading-relaxed text-ink" data-id="element-335" data-mp="18fatc17bzh1h">
            All through one structured workflow designed around a{" "}
            <strong data-id="element-336" data-mp="1ibnl2tjbfktl">15-minute initial review.</strong>
          </p>
          <p className="mt-3 text-center text-lg leading-relaxed text-ink-muted" data-id="element-337" data-mp="1ia2t9bodyfzf">
            Not a shortcut to diagnosis. A clearer way to organize the information that supports
            appropriate clinical reasoning.
          </p>
          <CTAButton className="mt-10" label="Get my 15-Minute Alopecia Assessment Framework™" note="Just $27 USD · Complete digital collection" data-id="element-338" data-mp="1mdr01s5717jm" />
        </div>
      </section>
    </>;
}